import { useLayoutEffect } from 'react'
import { REVEAL_LINE, revealRootMargin } from '../lib/reveal'

export function useRevealObserver(pathname: string) {
  useLayoutEffect(() => {
    const root = document.getElementById('root')
    if (!root) return

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      const revealAll = () => {
        root.querySelectorAll('[data-reveal]').forEach((node) => node.classList.add('is-in'))
      }
      revealAll()
      const mo = new MutationObserver(revealAll)
      mo.observe(root, { childList: true, subtree: true })
      return () => mo.disconnect()
    }

    const settle = (node: Element) => {
      node.addEventListener(
        'animationend',
        (event) => {
          if (!(event instanceof AnimationEvent)) return
          if (event.animationName !== 'hero-rise') return
          node.classList.add('is-settled')
        },
        { once: true },
      )
    }

    const revealNow = (node: HTMLElement) => {
      node.classList.add('is-in')
      settle(node)
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          if (!(entry.target instanceof HTMLElement)) continue
          revealNow(entry.target)
          io.unobserve(entry.target)
        }
      },
      { threshold: 0, rootMargin: revealRootMargin() },
    )

    let scanned = false

    const observeNew = () => {
      root.querySelectorAll('[data-reveal]').forEach((node) => {
        if (!(node instanceof HTMLElement) || node.closest('.hero-intro')) return
        if (node.classList.contains('is-in') || node.classList.contains('is-settled')) return
        const rect = node.getBoundingClientRect()
        const onScreen = rect.bottom > 0 && rect.top < window.innerHeight
        const pastLine = rect.top < window.innerHeight * REVEAL_LINE
        if (pastLine || (scanned && onScreen)) {
          revealNow(node)
          return
        }
        io.observe(node)
      })
    }

    observeNew()
    scanned = true
    const mo = new MutationObserver(observeNew)
    mo.observe(root, { childList: true, subtree: true })

    return () => {
      io.disconnect()
      mo.disconnect()
    }
  }, [pathname])
}
